"use client";

import Image from "next/image";
import Link from "next/link";
import Script from "next/script";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, RotateCcw, SearchCheck, ShieldCheck, UserRound } from "lucide-react";
import { fetchTaxonomyTerms, fetchTherapistMatches, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import type { ServiceDelivery } from "@/types/therapist";
import { AVAILABILITY_LABELS, SERVICE_DELIVERY_LABELS } from "@/types/therapist";

type Step = "safety" | "support" | "preferences" | "results";

const STEPS: Array<{ key: Step; label: string }> = [
  { key: "safety", label: "Start" },
  { key: "support", label: "Support" },
  { key: "preferences", label: "Preferences" },
  { key: "results", label: "Matches" },
];

export function TherapistMatcher() {
  const [step, setStep] = useState<Step>("safety");
  const [showCrisisHelp, setShowCrisisHelp] = useState(false);
  const [focusAreaIds, setFocusAreaIds] = useState<string[]>([]);
  const [modalityIds, setModalityIds] = useState<string[]>([]);
  const [demographicId, setDemographicId] = useState("");
  const [delivery, setDelivery] = useState<ServiceDelivery | "">("");
  const [website, setWebsite] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string>();
  const [validationError, setValidationError] = useState<string>();
  const hcaptchaSiteKey = process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY;
  const terms = useQuery({
    queryKey: ["therapist-match-taxonomies"],
    queryFn: async () => (await Promise.all(
      ["FOCUS_AREA", "MODALITY", "DEMOGRAPHIC"].map((type) =>
        fetchTaxonomyTerms(type as "FOCUS_AREA" | "MODALITY" | "DEMOGRAPHIC")),
    )).flat(),
  });
  const match = useMutation({ mutationFn: fetchTherapistMatches });
  const grouped = useMemo(() => ({
    focus: (terms.data ?? []).filter((term) => term.type === "FOCUS_AREA"),
    modality: (terms.data ?? []).filter((term) => term.type === "MODALITY"),
    demographic: (terms.data ?? []).filter((term) => term.type === "DEMOGRAPHIC"),
  }), [terms.data]);
  const toggle = (values: string[], id: string, setter: (next: string[]) => void) =>
    setter(values.includes(id) ? values.filter((value) => value !== id) : [...values, id]);

  useEffect(() => {
    window.onWpCmsHcaptchaVerifyTherapistMatcher = (token: string) => setCaptchaToken(token);
    window.onWpCmsHcaptchaExpiredTherapistMatcher = () => setCaptchaToken(undefined);
    return () => {
      delete window.onWpCmsHcaptchaVerifyTherapistMatcher;
      delete window.onWpCmsHcaptchaExpiredTherapistMatcher;
    };
  }, []);

  const submit = () => {
    if (hcaptchaSiteKey && !captchaToken) {
      setValidationError("Complete the CAPTCHA before viewing recommendations.");
      return;
    }
    setValidationError(undefined);
    match.mutate({
      focusAreaIds,
      modalityIds,
      demographicId: demographicId || undefined,
      preferredDelivery: delivery || undefined,
      website,
      captchaToken,
    }, { onSuccess: () => setStep("results") });
  };

  const restart = () => {
    setStep("support");
    setFocusAreaIds([]);
    setModalityIds([]);
    setDemographicId("");
    setDelivery("");
    setValidationError(undefined);
    match.reset();
  };

  if (terms.isLoading) {
    return <p className="border-y border-dashed py-16 text-center text-sm text-slate-600">Loading matching options...</p>;
  }
  if (terms.isError) {
    return <p role="alert" className="border-y border-red-200 py-8 text-red-700">Matching options could not be loaded.</p>;
  }

  const stepIndex = STEPS.findIndex((item) => item.key === step);

  return (
    <div className="mx-auto max-w-4xl">
      <nav aria-label="Matching progress" className="mb-10">
        <ol className="grid grid-cols-4 gap-2">
          {STEPS.map((item, index) => (
            <li key={item.key} aria-current={item.key === step ? "step" : undefined}>
              <div className={`h-1.5 w-full ${index <= stepIndex ? "bg-[var(--color-primary)]" : "bg-slate-200"}`} />
              <p className={`mt-2 text-xs font-medium ${item.key === step ? "text-[var(--color-primary)]" : "text-slate-500"}`}>{item.label}</p>
            </li>
          ))}
        </ol>
      </nav>

      {step === "safety" && (
        <section aria-labelledby="matching-safety-title">
          <div className="flex items-start gap-4">
            <ShieldCheck className="mt-1 h-8 w-8 shrink-0 text-[var(--color-primary)]" aria-hidden />
            <div>
              <h2 id="matching-safety-title" className="text-2xl font-semibold">Before we begin</h2>
              <p className="mt-2 max-w-2xl leading-7 text-slate-600">This private tool compares your preferences with published therapist profiles. Your answers are not saved, and the results are not clinical advice or a guarantee of fit.</p>
            </div>
          </div>
          <div className="mt-8 border-y border-red-200 bg-red-50 px-5 py-5 text-red-950" role="note">
            <p className="font-semibold">Are you in immediate danger or thinking about harming yourself?</p>
            <p className="mt-1 text-sm">This matching tool cannot provide emergency care.</p>
            {showCrisisHelp && (
              <div className="mt-4 border-t border-red-200 pt-4 text-sm leading-6">
                <p><strong>Call or text 988</strong> for the Suicide &amp; Crisis Lifeline, call 911, or go to the nearest emergency room if you are in immediate danger.</p>
                <a href="tel:988" className="mt-3 inline-flex min-h-[44px] items-center font-semibold underline">Call 988 now</a>
              </div>
            )}
          </div>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setShowCrisisHelp(true)}>Yes, show help now</Button>
            <Button type="button" onClick={() => setStep("support")}><span>I am safe, continue</span><ChevronRight className="h-4 w-4" aria-hidden /></Button>
          </div>
        </section>
      )}

      {step === "support" && (
        <section aria-labelledby="matching-support-title">
          <h2 id="matching-support-title" className="text-2xl font-semibold">What kind of support are you looking for?</h2>
          <p className="mt-2 text-sm text-slate-600">Select one or more areas. These choices have the greatest influence on your matches.</p>
          <fieldset className="mt-6">
            <legend className="sr-only">Support areas</legend>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {grouped.focus.map((term) => <Choice key={term.id} label={term.label} checked={focusAreaIds.includes(term.id)} onChange={() => toggle(focusAreaIds, term.id, setFocusAreaIds)} />)}
            </div>
          </fieldset>
          <div className="mt-8 flex items-center justify-between">
            <Button type="button" variant="outline" onClick={() => setStep("safety")}><ChevronLeft className="h-4 w-4" aria-hidden />Back</Button>
            <Button type="button" disabled={focusAreaIds.length === 0} onClick={() => setStep("preferences")}>Continue<ChevronRight className="h-4 w-4" aria-hidden /></Button>
          </div>
        </section>
      )}

      {step === "preferences" && (
        <section aria-labelledby="matching-preferences-title">
          <h2 id="matching-preferences-title" className="text-2xl font-semibold">Tell us what would make care feel like a better fit</h2>
          <p className="mt-2 text-sm text-slate-600">These preferences are optional. Leave anything blank that does not matter to you.</p>
          <fieldset className="mt-7">
            <legend className="text-base font-semibold">Preferred approaches</legend>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {grouped.modality.map((term) => <Choice key={term.id} label={term.label} checked={modalityIds.includes(term.id)} onChange={() => toggle(modalityIds, term.id, setModalityIds)} />)}
            </div>
          </fieldset>
          <div className="mt-7 grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="match-demographic" className="text-base font-semibold">Community or population</label>
              <select id="match-demographic" className="mt-2 min-h-[44px] w-full border border-[var(--color-border)] bg-white px-3" value={demographicId} onChange={(event) => setDemographicId(event.target.value)}><option value="">No preference</option>{grouped.demographic.map((term) => <option key={term.id} value={term.id}>{term.label}</option>)}</select>
            </div>
            <div>
              <label htmlFor="match-delivery" className="text-base font-semibold">Appointment format</label>
              <select id="match-delivery" className="mt-2 min-h-[44px] w-full border border-[var(--color-border)] bg-white px-3" value={delivery} onChange={(event) => setDelivery(event.target.value as ServiceDelivery | "")}><option value="">No preference</option>{Object.entries(SERVICE_DELIVERY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
            </div>
          </div>
          {hcaptchaSiteKey && <div className="mt-7"><Script id="hcaptcha-therapist-matcher" src="https://js.hcaptcha.com/1/api.js" async defer /><div className="h-captcha" data-sitekey={hcaptchaSiteKey} data-callback="onWpCmsHcaptchaVerifyTherapistMatcher" data-expired-callback="onWpCmsHcaptchaExpiredTherapistMatcher" /></div>}
          <div className="absolute left-[-10000px]" aria-hidden><label htmlFor="match-website">Website</label><input id="match-website" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} /></div>
          {validationError && <p role="alert" className="mt-4 text-sm text-red-700">{validationError}</p>}
          {match.isError && <p role="alert" className="mt-4 text-sm text-red-700">{formatApiError(match.error, "Matches could not be calculated.")}</p>}
          <div className="mt-8 flex items-center justify-between">
            <Button type="button" variant="outline" onClick={() => setStep("support")}><ChevronLeft className="h-4 w-4" aria-hidden />Back</Button>
            <Button type="button" disabled={match.isPending} onClick={submit}><SearchCheck className="h-4 w-4" aria-hidden />{match.isPending ? "Finding matches..." : "Show my matches"}</Button>
          </div>
        </section>
      )}

      {step === "results" && (
        <section aria-live="polite" aria-labelledby="matching-results-title">
          <div className="flex flex-col gap-4 border-b border-[var(--color-border)] pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div><p className="text-sm font-semibold text-[var(--color-primary)]">Personalized results</p><h2 id="matching-results-title" className="mt-1 text-2xl font-semibold">Your therapist matches</h2></div>
            <Button type="button" variant="outline" onClick={restart}><RotateCcw className="h-4 w-4" aria-hidden />Start over</Button>
          </div>
          {!match.data?.matches.length ? (
            <div className="border-b border-dashed py-16 text-center"><UserRound className="mx-auto h-9 w-9 text-slate-400" aria-hidden /><p className="mt-3 font-medium">No available matches right now</p><Link href="/therapists" className="mt-2 inline-flex min-h-[44px] items-center text-sm font-semibold text-[var(--color-primary)]">Browse the full directory</Link></div>
          ) : (
            <ol className="divide-y divide-[var(--color-border)]">
              {match.data.matches.map((person) => (
                <li key={person.therapistId} className="grid gap-5 py-6 sm:grid-cols-[128px_1fr]">
                  <div className="relative aspect-[4/3] bg-slate-100 sm:aspect-auto sm:min-h-40">{person.photoUrl ? <Image src={person.photoUrl} alt="" fill unoptimized className="object-cover object-top" /> : <UserRound className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 text-slate-300" aria-hidden />}</div>
                  <div><div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"><div><p className="text-xs font-semibold text-[var(--color-primary)]">Match #{person.rank}</p><h3 className="text-xl font-semibold">{person.firstName} {person.lastName}</h3>{person.credentials && <p className="text-sm text-slate-500">{person.credentials}</p>}</div><span className="text-sm font-medium text-slate-600">{AVAILABILITY_LABELS[person.availabilityStatus]}</span></div><ul className="mt-4 space-y-2 text-sm text-slate-600">{person.explanations.map((reason) => <li key={reason} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />{reason}</li>)}</ul><Link href={`/therapists/${person.slug}`} className="mt-4 inline-flex min-h-[44px] items-center font-semibold text-[var(--color-primary)]">View profile<ChevronRight className="ml-1 h-4 w-4" aria-hidden /></Link></div>
                </li>
              ))}
            </ol>
          )}
          <p className="mt-5 text-xs leading-5 text-slate-500">These results are informational and are not clinical advice, diagnosis, or a guarantee of fit. Contact the practice or a qualified professional for help choosing care.</p>
        </section>
      )}
    </div>
  );
}

function Choice({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return <label className={`flex min-h-[48px] cursor-pointer items-center gap-3 border px-3 text-sm ${checked ? "border-[var(--color-primary)] bg-[var(--color-surface-muted)]" : "border-[var(--color-border)] bg-white"}`}><input type="checkbox" checked={checked} onChange={onChange} />{label}</label>;
}
